import React, { useState } from 'react'
import styles from './Hero.module.scss'

interface RotatingWord {
  prefix: string
  word: string
  color: string
}

interface JourneyStep {
  icon: string
  title: string
  desc: string
}

const rotatingWords: RotatingWord[] = [
  { prefix: 'We enable', word: 'entrepreneurship', color: '#4d8bff' },
  { prefix: 'We ensure', word: 'growth', color: '#15d3b9' },
  { prefix: 'Enroute', word: 'incubation', color: '#b06bff' },
  { prefix: 'We enable', word: 'entrepreneurship', color: '#4d8bff' },
]

const journeySteps: JourneyStep[] = [
  { icon: '/logo2.svg', title: 'Start your journey', desc: 'with Socommerz' },
  {
    icon: '/ignite.svg',
    title: 'Ignite',
    desc: 'Your product development strategies.',
  },
  {
    icon: '/build-product.svg',
    title: 'Build product',
    desc: 'Build your product with the latest tech stack.',
  },
  {
    icon: '/build-and-nurture.svg',
    title: 'Team Augmentations',
    desc: "With CXO's and Engineering.",
  },
  {
    icon: '/direction.svg',
    title: 'Direction',
    desc: 'Defining your growth strategies.',
  },
  {
    icon: '/help-to-raise.svg',
    title: 'Help to Raise',
    desc: 'Connect with our HNI network and domain experts.',
  },
]

const RADIUS_PCT = 33 // node distance from centre, as % of the orbit box

const Hero = () => {
  const [active, setActive] = useState(0)
  const activeStep = journeySteps[active]

  return (
    <section className={styles.hero}>
      {/* animated background */}
      <div className={styles.bg} aria-hidden='true'>
        <span className={`${styles.orb} ${styles.orb1}`} />
        <span className={`${styles.orb} ${styles.orb2}`} />
        <span className={`${styles.orb} ${styles.orb3}`} />
        <div className={styles.grid} />
      </div>

      <div className={styles.inner}>
        {/* ---------------- Left: headline ---------------- */}
        <div className={styles.headline}>
          <p className={styles.eyebrow}>
            For innovators, ideators &amp; disrupters
          </p>
          <div className={styles.words}>
            {rotatingWords.map((w, i) => (
              <span className={styles.sp} key={i}>
                <span className={styles.prefix}>{w.prefix}&nbsp;</span>
                <span className={styles.accent} style={{ color: w.color }}>
                  {w.word}
                </span>
              </span>
            ))}
          </div>

          <p className={styles.subtitle}>
            Socommerz builds and invests in global tech ventures. We support
            founders with operational expertise and growth guidance.
          </p>

          {/* <a href='#journey' className={styles.cta}>
            Start your journey
            <span className={styles.ctaArrow}>→</span>
          </a> */}
        </div>

        {/* ---------------- Right: orbit ---------------- */}
        <div className={styles.orbitCol} id='journey'>
          <div className={styles.orbit}>
            <span
              className={`${styles.ring} ${styles.ring1}`}
              aria-hidden='true'
            />
            <span
              className={`${styles.ring} ${styles.ring2}`}
              aria-hidden='true'
            />

            {/* spokes from hub to each node */}
            <svg
              className={styles.spokes}
              viewBox='0 0 100 100'
              preserveAspectRatio='none'
              aria-hidden='true'
            >
              {journeySteps.map((_, i) => {
                const a = ((-90 + i * 60) * Math.PI) / 180
                const x = 50 + RADIUS_PCT * Math.cos(a)
                const y = 50 + RADIUS_PCT * Math.sin(a)
                return (
                  <line
                    key={i}
                    x1='50'
                    y1='50'
                    x2={x}
                    y2={y}
                    className={`${styles.spoke} ${
                      active === i ? styles.spokeActive : ''
                    }`}
                  />
                )
              })}
            </svg>

            {/* center hub — reflects the active step */}
            <div className={styles.hub}>
              <img src={activeStep.icon} alt='' className={styles.hubIcon} />
              <p className={styles.hubTitle}>{activeStep.title}</p>
              <p className={styles.hubDesc}>{activeStep.desc}</p>
            </div>

            {/* orbiting nodes */}
            {journeySteps.map((step, i) => {
              const a = ((-90 + i * 60) * Math.PI) / 180
              const x = 50 + RADIUS_PCT * Math.cos(a)
              const y = 50 + RADIUS_PCT * Math.sin(a)
              return (
                <button
                  key={step.title}
                  type='button'
                  className={`${styles.orbitNode} ${
                    active === i ? styles.nodeActive : ''
                  }`}
                  style={{ left: `${x}%`, top: `${y}%` }}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  aria-label={step.title}
                >
                  <span className={styles.nodeCircle}>
                    <img src={step.icon} alt='' className={styles.nodeImg} />
                  </span>
                  <span className={styles.nodeLabel}>{step.title}</span>
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Hero
